import type { Balance } from '../types';
import { Currency } from '../types/generated';

interface CurrencyMeta {
  /** vfiat units per 1 display unit. Fiat: 100,000 (1 EUR = 100,000 vfiat). BTC: 100,000,000 (sats). */
  divisor: number;
  /** Default minimum fraction digits */
  minDigits: number;
  /** Default maximum fraction digits */
  maxDigits: number;
  /** ISO 4217 code for fiat — used by Intl currency style. Undefined for crypto. */
  isoCode?: string;
  /** Crypto display: prefix symbol (e.g. ₿) */
  prefix?: string;
  /** Crypto display: suffix ticker (e.g. USDT) */
  suffix?: string;
}

const FIAT_DIVISOR = 100_000;
const SATS_PER_BTC = 100_000_000;

const CRYPTO_META: Partial<Record<Currency, CurrencyMeta>> = {
  [Currency.BTC]: { divisor: SATS_PER_BTC, minDigits: 2, maxDigits: 8, prefix: '₿' },
  [Currency.USDT]: { divisor: FIAT_DIVISOR, minDigits: 2, maxDigits: 6, suffix: 'USDT' },
  [Currency.DAI]: { divisor: FIAT_DIVISOR, minDigits: 2, maxDigits: 6, suffix: 'DAI' },
};

function getCurrencyMeta(currency: Currency): CurrencyMeta {
  const crypto = CRYPTO_META[currency];
  if (crypto) return crypto;
  return {
    divisor: FIAT_DIVISOR,
    minDigits: 2,
    maxDigits: 2,
    isoCode: currency.toUpperCase(),
  };
}

export interface FormatCurrencyOptions {
  /** Override min fraction digits. */
  minFractionDigits?: number;
  /** Override max fraction digits. Pad/trim trailing zeros within [min, max]. */
  maxFractionDigits?: number;
  /** Locale for number formatting. Defaults to en-US. */
  locale?: string;
}

/** Convert a Balance from vfiat to its display unit. */
export function balanceNextHigher(bal: Balance) {
  if (bal[1] === 0) return 0;
  return bal[1] / getCurrencyMeta(bal[0]).divisor;
}

/**
 * Format a Balance for display. Fiat uses Intl currency style (e.g. "€1.50"),
 * crypto uses prefix/suffix conventions (e.g. "₿0.000012", "1.50 USDT").
 *
 * Examples:
 *   formatCurrency([Currency.EUR, 150_000])                        // "€1.50"
 *   formatCurrency([Currency.EUR, 1_400], { maxFractionDigits: 4 }) // "€0.0140"
 *   formatCurrency([Currency.BTC, 1_200])                          // "₿0.000012"
 *   formatCurrency([Currency.USDT, 5_000_000])                     // "50.00 USDT"
 */
export function formatCurrency(bal: Balance, options: FormatCurrencyOptions = {}): string {
  const [currency, amount] = bal;
  const meta = getCurrencyMeta(currency);
  const value = amount / meta.divisor;
  const minDigits = options.minFractionDigits ?? meta.minDigits;
  const maxDigits = Math.max(options.maxFractionDigits ?? meta.maxDigits, minDigits);
  const locale = options.locale ?? 'en-US';

  if (meta.isoCode) {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: meta.isoCode,
      minimumFractionDigits: minDigits,
      maximumFractionDigits: maxDigits,
    }).format(value);
  }

  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: minDigits,
    maximumFractionDigits: maxDigits,
  }).format(value);
  if (meta.prefix) return `${meta.prefix}${formatted}`;
  if (meta.suffix) return `${formatted} ${meta.suffix}`;
  return formatted;
}
